// src/utils/disputesAdmin.js
// Payment Settings "Disputes" (admin only; STEP3_5A_TIERS_DESIGN.md 3.13.4,
// 3.13.5, 3.13.6 and 5.14; release R6).
//
// Display and payload shaping only; the server decides everything:
//  - adminListDisputes: open Stripe disputes (reason, amount, due date, our
//    purchase, the warning and 3D Secure facts, the suggested action) and
//    the fraudWarnings rows that were not refunded automatically;
//  - adminBuildDisputeEvidence: the evidence draft for one dispute;
//  - adminDisputeAction({disputeId | efwId, action}): draft, submit, accept,
//    efw_refund, efw_refund_fraud, apply_hold (each writes an audit row);
//  - adminSetTipLimitConfig: the three settings blocks, each a PARTIAL
//    update without mode ({efw}, {threeDS}, {join}) with its own note
//    (audit efw_config, three_ds_config, join_gate_config).
// The admin strings are 5.14's, word for word. The reason labels, the
// not-eligible warning chip, the auto-refund hint and the 3D Secure "Off
// (Stripe decides)" label are the R6 copy read's suggested defaults (12.2
// item 8, approved 10-18), since 5.14 gives none.
//
// The list normalizer reads the documented names first and a few obvious
// alternatives, so a field renamed on the server degrades to a blank, never
// to a crash.
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-membership-join-v2.mjs). ASCII only (the middle
// dot is \u00b7).

const DOT = ' \u00b7 ';

export const DISPUTES_COPY = Object.freeze({
  title: 'Disputes',
  empty: 'No open disputes or fraud warnings.',
  // Dispute row chips.
  chipEfw: 'Issuer fraud warning',
  chipThreeDS: 'Bank confirmed (3D Secure)',
  chipActivity: 'Member activity after the charge',
  // Fraud warning row chips.
  chipOverAmount: 'Over the automatic amount',
  chipRefundsOff: 'Automatic refunds off',
  chipNotEligible: 'Not a membership, Super Crit or Rumble',
  chipVerifiedNoHold: 'Verified account, no hold',
  // Suggested action.
  suggestAccept: 'Suggested: accept (likely a stolen card)',
  suggestEvidence: 'Suggested: respond with evidence',
  suggestThreeDS: 'Suggested: respond with the 3D Secure result',
  suggestSmart: 'Suggested: leave to Smart Disputes (under $15)',
  // Buttons.
  buildPacket: 'Build packet',
  saveDraft: 'Save draft to Stripe',
  submit: 'Submit to Stripe',
  accept: 'Accept',
  refund: 'Refund',
  refundFraud: 'Refund as fraud',
  applyHold: 'Apply hold',
  // Confirms.
  confirmSubmit: (amount) => `Submit this evidence to Stripe for the ${amount} dispute? You can't change it after submitting.`,
  confirmAccept: (amount) => `Accept this ${amount} dispute? The money stays with the cardholder and the $15 fee is not returned.`,
  confirmRefund: (amount) => `Refund ${amount}? The card stays blocked here for Super Crits, Rumbles and new memberships.`,
  confirmRefundFraud: "Refund as fraud? Stripe will then decline this card, every other card saved on this customer, and its email address for any payment on CritCast, Plans included. Our Unblock can't undo that; removing them is done in the Stripe Dashboard under Radar, Lists. Continue?",
  confirmApplyHold: 'Apply a hold to this account? It stops purchases, event entry, team changes, payouts and serving its videos until you clear it in Payment Settings.',
  // R6 review R6C-5: a dispute Stripe no longer takes a response for shows
  // its status in place of "respond by" (admin only; for the founder's read).
  statusSubmitted: 'Submitted',
  statusWon: 'Won',
  statusLost: 'Lost',
  statusClosed: 'Closed',
  // R6 review R6-DARK-3: a warning the webhook never finished (sent before the
  // live endpoint carried the event, or during a pin below R6). Admin only;
  // for the founder's read.
  chipNotHandled: 'Not handled yet',
  handleNow: 'Handle now',
  confirmHandleNow: (amount) => `Handle this ${amount} fraud warning now? It runs the same steps as a warning that just arrived: the automatic refund rule, the card block, the hold and the memberships on the card.`,
  // Issuer fraud warnings block.
  efwTitle: 'Issuer fraud warnings',
  efwSwitch: 'Refund reported payments automatically',
  efwMax: 'Refund automatically up to (dollars, 0 to 500)',
  efwHint: 'While the month has fewer than 100 card payments, every reported payment is refunded automatically, unless the bank confirmed it with 3D Secure.',
  // 3D Secure block.
  threeDSTitle: 'Ask the bank to confirm (3D Secure)',
  threeDSTips: 'Checkout Super Crits and Rumbles',
  // "Off" leaves it to Stripe, and the bank can still ask (R6 copy read 12.2
  // item 8, approved 10-18).
  scopeLabels: Object.freeze({ unverified: 'Unverified accounts', all: 'Everyone', off: 'Off (Stripe decides)' }),
  // Join gate block.
  joinTitle: 'Membership joins',
  joinEmail: 'Unverified accounts need a confirmed email',
  joinWait: 'Waiting period for new unverified accounts (hours, 0 to 168)',
  joinFlow: 'Join screen',
  flowLabels: Object.freeze({ element: 'Payment form', checkout: 'Stripe Checkout (emergency)' }),
  note: 'Note (required)',
  save: 'Save',
});

export const SCOPES = Object.freeze(['unverified', 'all', 'off']);
export const FLOWS = Object.freeze(['element', 'checkout']);

// Stripe's dispute reasons -> readable labels (R6 copy read 12.2 item 8).
export const REASON_LABELS = Object.freeze({
  fraudulent: 'Fraud',
  unrecognized: 'Not recognized',
  subscription_canceled: 'Canceled membership',
  product_not_received: 'Not received',
  product_unacceptable: 'Not as described',
  credit_not_processed: 'Credit not processed',
  duplicate: 'Duplicate',
  general: 'General',
});

export function reasonLabel(reason) {
  if (typeof reason !== 'string' || !reason) return 'Dispute';
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  const words = reason.replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Dispute';
}

export const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
export const fmtDate = (ms) => {
  const n = Number(ms);
  return Number.isFinite(n) && n > 0
    ? new Date(n).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'an unknown date';
};

const num = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (v != null && v !== '' && Number.isFinite(n)) return n;
  }
  return null;
};
const str = (...vals) => vals.find((v) => typeof v === 'string' && v) || null;
// Seconds (Stripe) or milliseconds -> milliseconds.
const msOf = (v) => {
  const n = num(v);
  if (n == null || n <= 0) return null;
  return n < 1e11 ? n * 1000 : n;
};

// The suggestion key -> its 5.14 line. The server's keys (disputeEvidence.js
// suggestionOf) are 'accept' | 'respond_evidence' | 'respond_3ds' |
// 'smart_disputes'; a few other spellings read the same; null for none.
const SUGGESTIONS = {
  accept: 'suggestAccept',
  respond_evidence: 'suggestEvidence',
  evidence: 'suggestEvidence',
  respond: 'suggestEvidence',
  respond_3ds: 'suggestThreeDS',
  three_ds: 'suggestThreeDS',
  '3ds': 'suggestThreeDS',
  smart_disputes: 'suggestSmart',
  smart: 'suggestSmart',
};

// What a disputed or reported payment was (an admin meta line).
const KIND_LABELS = {
  membership: 'Membership',
  team_membership: 'Team membership',
  super_crit: 'Super Crit',
  team_super_crit: 'Squad Super Crit',
  rumble: 'Rumble',
};
export function suggestionLine(key) {
  const k = typeof key === 'string' ? SUGGESTIONS[key] : null;
  return k ? DISPUTES_COPY[k] : null;
}

// Statuses Stripe still takes a response for.
const RESPONDABLE = new Set(['needs_response', 'warning_needs_response']);

/**
 * adminListDisputes' reply ({testMode, disputes, hasMore, warnings};
 * disputeEvidence.js) -> { testMode, disputes, warnings, truncated }.
 * dispute: { id, reason, reasonText, amountCents, dueByMs, status,
 *   respondable, efw, threeDS, activity, suggestion, chargeId,
 *   paymentIntentId, purchaseLabel }.
 * warning: { id, amountCents, brand, last4, atMs, refundSkipped,
 *   payerVerified, holdApplied, actionable, refunded, chargeId, importable,
 *   decided, actions }.
 * actions: the server's list when it sends one ('efw_refund',
 * 'efw_refund_fraud', 'apply_hold', 'efw_import'), else the 3.13.5 rule
 * (warningActions).
 */
export function normalizeDisputeList(data) {
  const d = data && typeof data === 'object' ? data : {};
  const rawDisputes = Array.isArray(d.disputes) ? d.disputes : [];
  const rawWarnings = Array.isArray(d.warnings) ? d.warnings : (Array.isArray(d.fraudWarnings) ? d.fraudWarnings : []);
  const disputes = rawDisputes.filter((x) => x && typeof x === 'object').map((x) => {
    const id = str(x.disputeId, x.id);
    const status = str(x.status) || null;
    const threeDS = x.threeDSAuthenticated === true || x.threeDS === true
      || (x.threeDS && typeof x.threeDS === 'object' && x.threeDS.result === 'authenticated')
      || x.threeDSResult === 'authenticated';
    const kindLabel = typeof x.kind === 'string' ? KIND_LABELS[x.kind] || null : null;
    return {
      id,
      reason: str(x.reason) || null,
      reasonText: reasonLabel(x.reason),
      amountCents: num(x.amountCents, x.amount) || 0,
      dueByMs: msOf(num(x.dueByMs, x.respondByMs, x.evidenceDueByMs, x.evidence_details?.due_by)),
      status,
      respondable: status ? RESPONDABLE.has(status) : true,
      efw: x.warning === true || x.efw === true || x.hasFraudWarning === true || !!str(x.efwId)
        || (Array.isArray(x.warningIds) && x.warningIds.length > 0),
      threeDS: !!threeDS,
      activity: x.memberActivity === true || x.memberActivityAfterCharge === true,
      suggestion: str(x.suggestion, x.suggested),
      chargeId: str(x.chargeId, x.charge),
      paymentIntentId: str(x.paymentIntentId, x.payment_intent),
      purchaseLabel: str(x.purchaseLabel) || (kindLabel ? [kindLabel, str(x.creatorName)].filter(Boolean).join(DOT) : null),
    };
  }).filter((x) => x.id);
  const warnings = rawWarnings.filter((x) => x && typeof x === 'object').map((x) => {
    const w = {
      id: str(x.efwId, x.id),
      amountCents: num(x.amountCents, x.amount) || 0,
      brand: str(x.brand, x.card && x.card.brand) || 'Card',
      last4: str(x.last4, x.card && x.card.last4) || '----',
      atMs: msOf(num(x.atMs, x.createdAtMs, x.created)),
      refundSkipped: str(x.refundSkipped) || null,
      payerVerified: x.payerVerified === true,
      holdApplied: x.holdApplied === true,
      actionable: x.actionable !== false,
      refunded: x.action === 'refunded' || x.refunded === true || !!str(x.refundId),
      chargeId: str(x.chargeId, x.charge),
      // R6 review R6-DARK-3: never finished by the webhook; decided = a
      // stored decision exists (the row offers more than the import).
      importable: x.importable === true,
      decided: Array.isArray(x.actions) ? x.actions.some((a) => a !== 'efw_import') : x.importable !== true,
    };
    w.actions = Array.isArray(x.actions)
      ? {
        refund: x.actions.includes('efw_refund'),
        refundFraud: x.actions.includes('efw_refund_fraud'),
        applyHold: x.actions.includes('apply_hold'),
        handleNow: x.actions.includes('efw_import'),
      }
      : warningActions(w);
    return w;
  }).filter((x) => x.id);
  return {
    testMode: typeof d.testMode === 'boolean' ? d.testMode : null,
    disputes,
    warnings,
    truncated: d.truncated === true || d.hasMore === true,
  };
}

// `${reason} \u00b7 ${amount} \u00b7 respond by ${date}`
// A dispute that takes no response (R6 review R6C-5) shows its status there instead.
const STATUS_WORDS = {
  under_review: 'statusSubmitted',
  warning_under_review: 'statusSubmitted',
  won: 'statusWon',
  lost: 'statusLost',
  warning_closed: 'statusClosed',
};
export function disputeStatusWord(dp) {
  return DISPUTES_COPY[STATUS_WORDS[dp && dp.status] || 'statusClosed'];
}
export function disputeRowLine(dp) {
  if (!dp.respondable) return `${dp.reasonText}${DOT}${fmtUsd(dp.amountCents)}${DOT}${disputeStatusWord(dp)}`;
  return `${dp.reasonText}${DOT}${fmtUsd(dp.amountCents)}${DOT}respond by ${fmtDate(dp.dueByMs)}`;
}

// The empty line counts only what still needs the founder: disputes Stripe
// takes a response for or is reviewing, and the warnings (R6 review R6C-5).
const OPEN_STATUSES = new Set(['needs_response', 'warning_needs_response', 'under_review', 'warning_under_review']);
export function hasOpenItems(list) {
  const l = list && typeof list === 'object' ? list : {};
  const disputes = Array.isArray(l.disputes) ? l.disputes : [];
  const warnings = Array.isArray(l.warnings) ? l.warnings : [];
  return warnings.length > 0 || disputes.some((dp) => dp.respondable || OPEN_STATUSES.has(dp.status));
}

export function disputeChips(dp) {
  const out = [];
  if (dp.efw) out.push(DISPUTES_COPY.chipEfw);
  if (dp.threeDS) out.push(DISPUTES_COPY.chipThreeDS);
  if (dp.activity) out.push(DISPUTES_COPY.chipActivity);
  return out;
}

// `Fraud warning \u00b7 ${amount} \u00b7 ${brand} ending ${last4} \u00b7 ${date}`
export function warningRowLine(w) {
  return `Fraud warning${DOT}${fmtUsd(w.amountCents)}${DOT}${w.brand} ending ${w.last4}${DOT}${fmtDate(w.atMs)}`;
}

// The reason chip (why it was not refunded automatically), then the hold chip.
const SKIP_CHIPS = {
  three_ds: 'chipThreeDS',
  threshold: 'chipOverAmount',
  switch_off: 'chipRefundsOff',
  not_eligible: 'chipNotEligible',
};
export function warningChips(w) {
  const out = [];
  if (w.importable) out.push(DISPUTES_COPY.chipNotHandled);
  const k = w.refundSkipped ? SKIP_CHIPS[w.refundSkipped] : null;
  if (k) out.push(DISPUTES_COPY[k]);
  if (w.payerVerified && !w.holdApplied) out.push(DISPUTES_COPY.chipVerifiedNoHold);
  return out;
}

// What a warning row offers: a refund (plain or as fraud) while the charge
// can still be refunded; "Apply hold" for a verified payer with no hold; a
// warning the webhook never finished also offers "Handle now", and only that
// when no decision was stored (R6 review R6-DARK-3).
export function warningActions(w) {
  if (w.importable && !w.decided) return { refund: false, refundFraud: false, applyHold: false, handleNow: true };
  const canRefund = w.actionable && !w.refunded;
  return { refund: canRefund, refundFraud: canRefund, applyHold: w.payerVerified && !w.holdApplied, handleNow: !!w.importable };
}

// ---------------------------------------------------------------------------
// The settings blocks (partial adminSetTipLimitConfig updates, 3.13.6)
// ---------------------------------------------------------------------------

/** The three drafts from the stored config (configOut). */
export function settingsDrafts(config) {
  const c = config && typeof config === 'object' ? config : {};
  const efw = c.efw || {};
  const t = c.threeDS || {};
  const j = c.join || {};
  const max = num(efw.autoRefundMaxCents);
  const hours = num(j.minAccountAgeHours);
  return {
    efw: {
      autoRefund: efw.autoRefund !== false,
      maxDollars: max == null ? '20' : String(max / 100),
      note: '',
    },
    threeDS: {
      tips: SCOPES.includes(t.tips) ? t.tips : 'unverified',
      note: '',
    },
    join: {
      emailRequired: j.emailRequired !== false,
      hours: hours == null ? '0' : String(hours),
      flow: FLOWS.includes(j.flow) ? j.flow : 'element',
      note: '',
    },
  };
}

const noteOf = (v) => String(v || '').trim();

// A typed number, or the text as typed (the server refuses a non-number
// with its own sentence).
function typedNumber(text, scale = 1) {
  const t = String(text == null ? '' : text).replace(/[$,\s]/g, '');
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * scale) : t;
}

/** {efw: {autoRefund, autoRefundMaxCents}, note}; the field is in dollars. */
export function efwPayload(draft) {
  const max = typedNumber(draft?.maxDollars, 100);
  return {
    efw: { autoRefund: draft?.autoRefund === true, ...(max !== undefined ? { autoRefundMaxCents: max } : {}) },
    note: noteOf(draft?.note),
  };
}

/** {threeDS: {tips}, note}. */
export function threeDSPayload(draft) {
  return {
    threeDS: {
      tips: SCOPES.includes(draft?.tips) ? draft.tips : 'unverified',
    },
    note: noteOf(draft?.note),
  };
}

/** {join: {emailRequired, minAccountAgeHours, flow}, note}. */
export function joinPayload(draft) {
  const hours = typedNumber(draft?.hours, 1);
  return {
    join: {
      emailRequired: draft?.emailRequired === true,
      ...(hours !== undefined ? { minAccountAgeHours: hours } : {}),
      flow: FLOWS.includes(draft?.flow) ? draft.flow : 'element',
    },
    note: noteOf(draft?.note),
  };
}
